// Model Route data layer — all API↔UI shape mapping lives here;
// components consume UI shapes only and never touch CanvasAPI directly.

import dayjs from 'dayjs'

import { CanvasAPI } from '@/api/canvas'
import {
  AIGWRouteBinding,
  AIGWRouteResource,
  CanvasRouteType,
  ModelCatalogItem,
} from '@/api/canvas/types'
import {
  decodeModelFeatureLabels,
  encodeModelFeatureLabels,
  protocolLabel,
  routeStatusToApi,
} from '@/models/modelRoute'
import { formatDatePreset, formatDecimalString, formatMsToSeconds } from '@/utils/formatter'

import {
  CatalogModel,
  ModelRoute,
  ModelRouteKpi,
  Paginated,
  RouteApiAccess,
  RouteBillingRecord,
  RouteBillingSummary,
  RouteModel,
  RouteModelType,
  RouteMonitorSummary,
  RouteStatus,
} from './types'

/**
 * Disabled bindings (upstream_status 2) are never hard-deleted server-side and stay in
 * bindings[] forever — the UI treats them as removed everywhere (list model column,
 * Model List tab, API Access modes, billing filter options, edit prefill).
 */
function activeBindings(route: AIGWRouteResource): AIGWRouteBinding[] {
  return (route.bindings ?? []).filter((binding) => binding.upstream_status === 1)
}

async function fetchVendorNames(): Promise<Map<number, string>> {
  const vendors = await CanvasAPI.listModelVendors()
  return new Map(vendors.map((vendor) => [vendor.id, vendor.name]))
}

function paginate<T>(items: T[], page: number, pageSize: number): Paginated<T> {
  return {
    items: items.slice((page - 1) * pageSize, page * pageSize),
    total: items.length,
  }
}

function mapRouteToUi(route: AIGWRouteResource): ModelRoute {
  const bindings = activeBindings(route)
  const firstModel = bindings[0]?.model_name ?? '-'
  const model = bindings.length > 1 ? `${firstModel} +${bindings.length - 1}` : firstModel

  return {
    resID: route.route_id,
    // `name` on route items is the route's own display name (the spec example
    // misleadingly shows the AIGW service name)
    name: route.name,
    model,
    status: route.route_status === 1 ? 'activated' : 'suspended',
    cost: formatDecimalString(route.cost ?? '0', 3, 3),
    createdAt: route.created_at ? formatDatePreset(route.created_at, 'second').toString() : '-',
    lastModified: route.last_modified
      ? formatDatePreset(route.last_modified, 'second').toString()
      : '-',
    description: route.description ?? '',
  }
}

export function deleteModelRoute(routeResID: string): Promise<void> {
  return CanvasAPI.deleteRoute(routeResID)
}

export function fetchModelRouteKpi(): Promise<ModelRouteKpi> {
  // "Today" means the user's local calendar day — without from/to the backend
  // falls back to the UTC day, which drifts from the local times shown in Billing Detail.
  const now = dayjs()
  // TODO(model-route-api): summary has no all-statuses route count (total_routes only
  // counts activated) — taken from the unfiltered list pagination until backend adds one
  return Promise.all([
    CanvasAPI.getRouteKpiSummary({
      from: now.startOf('day').toISOString(),
      to: now.toISOString(),
    }),
    CanvasAPI.listRoutes({ page: 1, per_page: 1 }),
  ]).then(([summary, resources]) => ({
    totalRoutes: resources.pagination?.total ?? resources.routes.length,
    activatedRoutes: summary.activated_routes,
    todayRequests: summary.today_requests,
    avgResponseSec: formatMsToSeconds(summary.avg_response_ms),
    todayTokens: summary.today_tokens,
    todayCost: formatDecimalString(summary.today_cost, 0, 4),
  }))
}

export function fetchModelRoutes(params: {
  status: RouteStatus
  search?: string
  page: number
  pageSize: number
}): Promise<Paginated<ModelRoute>> {
  return CanvasAPI.listRoutes({
    status: routeStatusToApi(params.status),
    q: params.search?.trim() || undefined,
    page: params.page,
    per_page: params.pageSize,
  }).then((resources) => ({
    items: resources.routes.map(mapRouteToUi),
    total: resources.pagination?.total ?? resources.routes.length,
  }))
}

export function setModelRouteStatus(routeResID: string, status: RouteStatus): Promise<void> {
  return CanvasAPI.patchRouteStatus(routeResID, { status: routeStatusToApi(status) }).then(
    () => undefined,
  )
}

function mapBindingToRouteModel(
  binding: AIGWRouteBinding,
  vendorNames: Map<number, string>,
): RouteModel {
  // TODO(model-route-api): LB parent bindings may carry children[] — rendered flat as
  // the parent row until a display rule is decided
  return {
    model: binding.model_name,
    provider: vendorNames.get(binding.vendor) ?? '-',
    capabilities: decodeModelFeatureLabels(binding.caps.features),
    inputPrice: formatDecimalString(binding.price_config.input_token_price_per_million, 0, 4),
    outputPrice: formatDecimalString(binding.price_config.output_token_price_per_million, 0, 4),
  }
}

export function fetchRouteModels(
  routeResID: string,
  page: number,
  pageSize: number,
): Promise<Paginated<RouteModel>> {
  return Promise.all([CanvasAPI.getRouteDetail(routeResID), fetchVendorNames()]).then(
    ([route, vendorNames]) =>
      paginate(
        activeBindings(route).map((binding) => mapBindingToRouteModel(binding, vendorNames)),
        page,
        pageSize,
      ),
  )
}

const AUTH_MODE_LABELS: Record<string, string> = {
  apikey: 'Access Token',
  hmac: 'HMAC (AK/SK)',
  unsafe: 'Not configured',
}

const ACCESS_MODE_LABELS: Record<'openai' | 'claude', string[]> = {
  openai: ['OpenAI Chat Completions', 'OpenAI Completions', 'OpenAI Responses'],
  claude: ['Anthropic Messages'],
}

export function fetchRouteApiAccess(routeResID: string): Promise<RouteApiAccess> {
  return CanvasAPI.getRouteDetail(routeResID).then((route) => {
    const boundModes = new Set(activeBindings(route).flatMap((b) => b.modes))
    const accessModes = [...boundModes].flatMap((mode) => ACCESS_MODE_LABELS[mode] ?? [])

    const exampleProtocol = boundModes.has('claude') ? ('claude' as const) : ('openai' as const)
    const exampleUrl =
      exampleProtocol === 'claude'
        ? route.route_links.claude.messages
        : route.route_links.openai.chat_completions

    return {
      authMode: AUTH_MODE_LABELS[route.security_mode] ?? route.security_mode,
      accessModes,
      accessUrl: route.route_link,
      example: {
        url: exampleUrl,
        model: activeBindings(route)[0]?.model_name ?? 'MODEL_NAME',
        protocol: exampleProtocol,
      },
      credentialEnabled: route.credential_enabled,
    }
  })
}

/** Rotate the route credential; the returned plaintext is shown exactly once. */
export function rotateCredential(routeResID: string): Promise<{ apiKey: string }> {
  return CanvasAPI.rotateRouteCredential(routeResID).then((res) => {
    if (!res.api_key) {
      throw new Error('No API key returned by rotation')
    }
    return { apiKey: res.api_key }
  })
}

export function fetchRouteBillingSummary(routeResID: string): Promise<RouteBillingSummary> {
  return CanvasAPI.getRouteBillingSummary(routeResID).then((summary) => ({
    totalRequests: summary.total_requests,
    totalTokens: summary.total_tokens,
    totalCost: formatDecimalString(summary.total_cost, 3, 3),
  }))
}

/** v5: settled billing records across all routes (global Billing Detail page). */
export function fetchProjectBillingRecords(params: {
  /** Full public route ID filter. */
  route?: string
  /** Full public model ID filter. */
  model?: string
  routeType?: string
  from?: string
  to?: string
  page: number
  pageSize: number
}): Promise<Paginated<RouteBillingRecord>> {
  return CanvasAPI.listProjectBillingRecords({
    route: params.route,
    model: params.model,
    // Integer protocol filter (1|2|3); strings are rejected with 400.
    route_type: params.routeType ? (Number(params.routeType) as CanvasRouteType) : undefined,
    from: params.from,
    to: params.to,
    page: params.page,
    per_page: params.pageSize,
  }).then((res) => ({
    items: res.items.map((record) => ({
      route: record.route_name || '-',
      routeId: record.route_id,
      model: record.model_name,
      // TODO(model-route-api): the design wants service types (MaaS/Instance/…) here but
      // records only carry the protocol — shows protocol until backend adds model_type
      routeType: protocolLabel(record.route_type),
      endpoint: record.endpoint,
      tokenIn: record.input_tokens,
      tokenOut: record.output_tokens,
      latencySec: formatMsToSeconds(record.avg_response_ms),
      firstTokenSec: formatMsToSeconds(record.avg_first_token_ms),
      cost: formatDecimalString(record.cost),
      time: formatDatePreset(record.window_end, 'second').toString(),
    })),
    total: res.pagination.total,
  }))
}

/** Route filter options for the global billing page. */
export function fetchBillingRouteOptions(): Promise<{ label: string; value: string }[]> {
  // per_page cap is 100 — fine for current route counts; revisit if accounts grow past that
  return CanvasAPI.listRoutes({ page: 1, per_page: 100 }).then((resources) =>
    resources.routes.map((route) => ({ label: route.name, value: route.route_id })),
  )
}

/** Model filter options for the global billing page (whole catalog). */
export function fetchBillingModelOptions(): Promise<{ label: string; value: string }[]> {
  // TODO(model-route-api): records of models since removed from the catalog are not
  // listed here — needs a distinct-models source from backend
  return CanvasAPI.listModels({ page: 1, per_page: 50 }).then((res) =>
    res.items.map((model) => ({ label: model.name, value: model.model_id })),
  )
}

/** Protocol filter options; values are the integer bitmask as string for Dropdown compat. */
export const BILLING_PROTOCOL_OPTIONS = [
  { label: 'OpenAI', value: '1' },
  { label: 'Claude', value: '2' },
]

// --- Create / edit flow ---

/** Provider filter options (values are vendor ids as strings, for the /models `vendors` param). */
export function fetchProviderOptions(): Promise<{ label: string; value: string }[]> {
  return CanvasAPI.listModelVendors().then((vendors) =>
    vendors.map((vendor) => ({ label: vendor.name, value: String(vendor.id) })),
  )
}

function mapCatalogItem(item: ModelCatalogItem, vendorNames: Map<number, string>): CatalogModel {
  return {
    id: item.model_id,
    model: item.name,
    provider: vendorNames.get(item.vendor) ?? '-',
    capabilities: decodeModelFeatureLabels(item.caps.features),
    inputPrice: formatDecimalString(item.price_config.input_token_price_per_million, 0, 4),
    outputPrice: formatDecimalString(item.price_config.output_token_price_per_million, 0, 4),
    modes: item.caps.modes,
  }
}

export function fetchCatalogModels(params: {
  /** Not a server-side filter — the catalog is MaaS-only for now. */
  routeType: RouteModelType
  /** Vendor id as string (from fetchProviderOptions). */
  provider?: string
  capabilities?: string[]
  search?: string
  page: number
  pageSize: number
}): Promise<Paginated<CatalogModel>> {
  const caps = encodeModelFeatureLabels(params.capabilities ?? [])
  return Promise.all([
    CanvasAPI.listModels({
      status: 1,
      vendors: params.provider,
      caps: caps || undefined,
      q: params.search?.trim() || undefined,
      page: params.page,
      per_page: params.pageSize,
    }),
    fetchVendorNames(),
  ]).then(([res, vendorNames]) => ({
    items: res.items.map((item) => mapCatalogItem(item, vendorNames)),
    total: res.pagination.total,
  }))
}

export function createModelRoute(payload: {
  name: string
  description: string
  models: CatalogModel[]
}): Promise<{ resID: string; apiKey: string }> {
  return CanvasAPI.listRoutes({ page: 1, per_page: 1 }).then((resources) => {
    // Any AIGW from aigws[] is acceptable — no user selection needed by design
    const aigw = resources.aigws[0]
    if (!aigw) {
      throw new Error('No AIGW available for this account')
    }
    return CanvasAPI.createRoute({
      aigw_id: aigw.aigw_id,
      name: payload.name,
      description: payload.description || undefined,
      bindings: payload.models.map((model) => ({
        model_id: model.id,
        modes: model.modes,
        weight: 100,
        status: 1,
      })),
    }).then((route) => ({ resID: route.route_id, apiKey: route.api_key }))
  })
}

export interface RouteEditData {
  name: string
  description: string
  models: CatalogModel[]
}

export function fetchRouteForEdit(routeResID: string): Promise<RouteEditData> {
  return Promise.all([CanvasAPI.getRouteDetail(routeResID), fetchVendorNames()]).then(
    ([route, vendorNames]) => ({
      name: route.name,
      description: route.description ?? '',
      models: activeBindings(route).map((binding) => ({
        id: binding.model_id,
        model: binding.model_name,
        provider: vendorNames.get(binding.vendor) ?? '-',
        capabilities: decodeModelFeatureLabels(binding.caps.features),
        inputPrice: formatDecimalString(binding.price_config.input_token_price_per_million, 0, 4),
        outputPrice: formatDecimalString(binding.price_config.output_token_price_per_million, 0, 4),
        modes: binding.modes,
      })),
    }),
  )
}

export async function updateModelRoute(
  routeResID: string,
  next: RouteEditData,
  original: RouteEditData,
): Promise<void> {
  const originalIds = new Set(original.models.map((m) => m.id))
  const nextIds = new Set(next.models.map((m) => m.id))
  const added = next.models.filter((m) => !originalIds.has(m.id))
  const removed = original.models.filter((m) => !nextIds.has(m.id))

  // v5: one atomic batch — validated up front, single DB transaction, all-or-nothing.
  // Bindings cannot be hard-deleted; status 2 disables the model on this route.
  const bindings = [
    ...added.map((model) => ({
      model_id: model.id,
      modes: model.modes,
      weight: 100,
      status: 1 as const,
    })),
    ...removed.map((model) => ({
      model_id: model.id,
      modes: model.modes,
      status: 2 as const,
    })),
  ]

  if (bindings.length > 0) {
    await CanvasAPI.patchRouteBindings(routeResID, bindings)
  }

  if (next.name !== original.name || next.description !== original.description) {
    // `name` is required by the metadata API even for description-only edits
    await CanvasAPI.patchRouteMetadata(routeResID, {
      name: next.name,
      description: next.description,
    })
  }
}

export function fetchRouteMonitorSummary(routeResID: string): Promise<RouteMonitorSummary> {
  return CanvasAPI.getRouteMonitorSummary(routeResID).then((summary) => ({
    totalInputTokens: summary.input_tokens,
    totalOutputTokens: summary.output_tokens,
    totalCachedTokens: summary.cached_input_tokens,
    topModels: summary.top_models.map((model) => ({
      model: model.model_name,
      inputTokens: model.input_tokens,
      outputTokens: model.output_tokens,
      cachedTokens: model.cached_input_tokens,
    })),
  }))
}
